import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type {
  CategoryProductData,
  CategoryProductItem,
  CommandVariable,
  MessageButton,
} from '../../../../types/chat-message';
import {
  CategoryProductErrorIcon,
  CategoryProductFallback,
  CategoryProductSuccessIcon,
} from '../../../../assets/icon';
import { linkTextParts } from '../../../../utils';
import './CategoryProductCard.scss';

interface CategoryProductCardProps {
  data: CategoryProductData;
  canInteract: boolean;
  onButtonClick?: (name: string, id: number, isHidden?: boolean) => void | Promise<void>;
  onLinkClick?: (url: string) => void;
}

const hiddenMessage = (variables?: CommandVariable[]): boolean => (
  variables?.some((item) => item.name === 'hidden_message' && item.value === 'true') || false
);

const money = (value?: number): string => (
  value === undefined ? '--' : `${new Intl.NumberFormat('vi-VN').format(value)} ₫`
);

const ProductImage: React.FC<{ product: CategoryProductItem }> = ({ product }) => {
  const [hasError, setHasError] = React.useState(false);
  React.useEffect(() => { setHasError(false); }, [product.imageUrl]);
  const showProduct = !!product.imageUrl && !hasError;
  return <>
    <CategoryProductFallback className="category-product-card__image-fallback"
      role="img" aria-label="Không có ảnh sản phẩm" aria-hidden={showProduct} />
    {showProduct && <img className="category-product-card__image"
      src={product.imageUrl} alt={product.name} onError={() => setHasError(true)} />}
  </>;
};

const StatusIcon: React.FC<{ status?: boolean }> = ({ status }) => {
  if (status === undefined) return null;
  const label = status ? 'Tạo thành công' : 'Tạo thất bại';
  const Icon = status ? CategoryProductSuccessIcon : CategoryProductErrorIcon;
  return <Icon className="category-product-card__status" role="img" aria-label={label} />;
};

const ProductItem: React.FC<{ product: CategoryProductItem; index: number }> = ({ product, index }) => {
  const state = product.status === true ? 'success' : product.status === false ? 'error' : 'neutral';
  return (
    <li className={`category-product-card__item category-product-card__item--${state}`}>
      <div className="category-product-card__thumbnail">
        <ProductImage product={product} />
        <span className="category-product-card__index">{product.orderNumber ?? index + 1}</span>
      </div>
      <div className="category-product-card__details">
        <span className="category-product-card__name" title={product.name}>{product.name}</span>
        <span>Giá bán: {money(product.sellingPrice)}</span>
        <strong>Doanh số tổng: {money(product.totalRevenue)}</strong>
        {product.status === false && product.failureReason && <em>{product.failureReason}</em>}
      </div>
      <StatusIcon status={product.status} />
    </li>
  );
};

const MarkdownText: React.FC<{ children: string; className: string }> = ({ children, className }) => (
  <div className={className}><ReactMarkdown remarkPlugins={[remarkGfm]}>{children}</ReactMarkdown></div>
);

const LinkText: React.FC<{
  text: string;
  url: string;
  onClick?: (url: string) => void;
}> = ({ text, url, onClick }) => {
  const [before, after] = linkTextParts(text || url);
  return <div className="category-product-card__link-text">
    {before && <MarkdownText className="category-product-card__link-copy">{before}</MarkdownText>}
    {before && ' '}
    <button type="button" className="category-product-card__link" disabled={!url}
      onClick={() => onClick?.(url)}>tại đây</button>
    {after && <MarkdownText className="category-product-card__link-copy">{after}</MarkdownText>}
  </div>;
};

const SuggestionButton: React.FC<{
  button: MessageButton;
  onClick?: CategoryProductCardProps['onButtonClick'];
}> = ({ button, onClick }) => (
  <button type="button" className="category-product-card__button"
    onClick={() => onClick?.(button.name, button.id, hiddenMessage(button.commandVariables))}>
    {button.name}
  </button>
);

const CategoryProductCard: React.FC<CategoryProductCardProps> = ({
  data, canInteract, onButtonClick, onLinkClick,
}) => (
  <section className="category-product-card" aria-label="Danh sách sản phẩm gợi ý">
    {data.label && <MarkdownText className="category-product-card__label">{data.label}</MarkdownText>}
    <ol className="category-product-card__list">
      {data.products.map((product, index) => (
        <ProductItem key={product.id || `${product.name}-${index}`} product={product} index={index} />
      ))}
    </ol>
    {data.footerText && <MarkdownText className="category-product-card__footer">{data.footerText}</MarkdownText>}
    {data.links.map((link, index) => (
      <LinkText key={`${link.url}-${index}`} {...link} onClick={onLinkClick} />
    ))}
    {canInteract && data.buttons.length > 0 && (
      <div className="category-product-card__buttons">
        {data.buttons.map((button, index) => (
          <SuggestionButton key={button.id || index} button={button} onClick={onButtonClick} />
        ))}
      </div>
    )}
  </section>
);

export default CategoryProductCard;
